/* NOTE: Should only list the "main" button styles within this stylesheet for performance reasons.
         "Main" styles should be thought of as styles that we would want users of Develop using in their components.
         All others should live in `aha-button.global.less`.
 */

:host {
  --aha-button--line-height: 1.25rem;
  --aha-button--border-radius: var(--Common--border-radius);
  --aha-button--border-width: 1px;
  --aha-button--text-decoration: none;
  --aha-button--gap: var(--spacer);

  color: var(--aha-button--color);
  border-color: var(--aha-button--border-color);
  background-color: var(--aha-button--background-color);
  border-radius: var(--aha-button--border-radius);
  border-top-left-radius: var(--aha-button--top-left-radius, var(--aha-button--border-radius));
  border-top-right-radius: var(--aha-button--top-right-radius, var(--aha-button--border-radius));
  border-bottom-left-radius: var(
    --aha-button--bottom-left-radius,
    var(--aha-button--border-radius)
  );
  border-bottom-right-radius: var(
    --aha-button--bottom-right-radius,
    var(--aha-button--border-radius)
  );
  border-width: var(--aha-button--border-width);
  border-style: solid;
  min-width: 2rem;
  box-sizing: border-box;
  text-decoration: none;
  text-shadow: none;
  cursor: pointer;
  line-height: var(--aha-button--line-height);
  font-size: var(--aha-button--font-size);
  text-align: center;
  padding: var(--aha-button--padding-vertical) var(--aha-button--padding-horizontal);
  vertical-align: middle;
  display: inline-block;
  user-select: none;
  outline: none;
  position: relative;
}
#content {
  display: inline-flex;
  gap: var(--aha-button--gap);
  flex: 1 1;
  align-items: baseline;
}

:host([size='flex']) {
  line-height: inherit;
  min-width: auto;
  #content {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

:host([size='mini']) {
  --aha-button--padding-vertical: 0;
  --aha-button--padding-horizontal: 4px;
  --aha-button--font-size: 11px;
  min-width: 2em;
}
:host([size='small']) {
  --aha-button--padding-vertical: 2px;
  --aha-button--padding-horizontal: 10px;
  --aha-button--font-size: 11.9px;
}
:host([size='medium']) {
  --aha-button--padding-vertical: 4px;
  --aha-button--padding-horizontal: 12px;
  --aha-button--font-size: 0.8125rem;
}
:host([size='large']) {
  --aha-button--padding-vertical: 11px;
  --aha-button--padding-horizontal: 19px;
  --aha-button--font-size: 17.5px;
}

:host(:is(:focus-visible, [focused])) {
  box-shadow: var(--box-shadow-focus-ring);
}
:host([disabled]) {
  cursor: not-allowed;
  opacity: 0.5;
  filter: saturate(30%);
}

/* Default button */
:host([kind='default']) {
  --aha-button--color: var(--theme-primary-text);
  --aha-button--border-color: var(--theme-button-border);
  --aha-button--background-color: var(--theme-button);
  --aha-button--outline-color: var(--theme-primary-text);
}
:host-context(aha-menu[aria-expanded]) :host([kind='default']) {
  --aha-button--color: var(--theme-primary-text);
  --aha-button--border-color: var(--theme-button-border);
  --aha-button--background-color: var(--theme-button);
  --aha-button--outline-color: var(--theme-primary-text);
}
:host(
    [kind='default']:where(:not([disabled])):where(:focus-visible, [focused], :hover, [hovered])
  ) {
  --aha-button--border-color: var(--theme-button-border-hovered);
  --aha-button--background-color: var(--theme-button-hovered);
}
:host([kind='default']:where(:not([disabled])):where(:active, [active])) {
  --aha-button--border-color: var(--theme-button-border-hovered);
  --aha-button--background-color: var(--theme-button-active);
}

/* Primary button */
:host([kind='primary']) {
  --aha-button--color: var(--theme-light-text);
  --aha-button--border-color: var(--theme-button-primary);
  --aha-button--background-color: var(--theme-button-primary);
  --aha-button--outline-color: var(--aha-button--border-color);
}
:host-context(aha-menu[aria-expanded]) :host([kind='primary']) {
  --aha-button--color: var(--theme-light-text);
  --aha-button--border-color: var(--theme-button-primary);
  --aha-button--background-color: var(--theme-button-primary);
  --aha-button--outline-color: var(--aha-button--border-color);
}
:host(
    [kind='primary']:where(:not([disabled])):where(:focus-visible, [focused], :hover, [hovered])
  ) {
  --aha-button--border-color: var(--theme-button-primary-hovered);
  --aha-button--background-color: var(--theme-button-primary-hovered);
}
:host([kind='primary']:where(:not([disabled])):where(:active, [active])) {
  --aha-button--border-color: var(--theme-button-primary-active);
  --aha-button--background-color: var(--theme-button-primary-active);
}

/* Secondary button */
:host([kind='secondary']) {
  --aha-button--color: var(--theme-link-text);
  --aha-button--border-color: var(--theme-button-border);
  --aha-button--background-color: var(--theme-button);
  --aha-button--outline-color: var(--theme-link-text);
}
:host-context(aha-menu[aria-expanded]) :host([kind='secondary']) {
  --aha-button--color: var(--theme-link-text);
  --aha-button--border-color: var(--theme-button-border);
  --aha-button--background-color: var(--theme-button);
  --aha-button--outline-color: var(--theme-link-text);
}
:host(
    [kind='secondary']:where(:not([disabled])):where(:focus-visible, [focused], :hover, [hovered])
  ) {
  --aha-button--border-color: var(--theme-button-border-hovered);
  --aha-button--background-color: var(--theme-button-hovered);
}
:host([kind='secondary']:where(:not([disabled])):where(:active, [active])) {
  --aha-button--border-color: var(--theme-button-border-hovered);
  --aha-button--background-color: var(--theme-button-active);
}

/* Gray button */
:host([kind='gray']) {
  --aha-button--border-color: var(--theme-button-gray);
  --aha-button--background-color: var(--theme-button-gray);
  --aha-button--outline-color: var(--theme-primary-text);
}
:host-context(aha-menu[aria-expanded]) :host([kind='gray']) {
  --aha-button--border-color: var(--theme-button-gray);
  --aha-button--background-color: var(--theme-button-gray);
  --aha-button--outline-color: var(--theme-primary-text);
}
:host([kind='gray']:where(:not([disabled])):where(:focus-visible, [focused], :hover, [hovered])) {
  --aha-button--border-color: var(--theme-button-gray-hovered);
  --aha-button--background-color: var(--theme-button-gray-hovered);
}
:host([kind='gray']:where(:not([disabled])):where(:active, [active])) {
  --aha-button--border-color: var(--theme-button-gray-active);
  --aha-button--background-color: var(--theme-button-gray-active);
}

/* Success button */
:host([kind='success']) {
  --aha-button--color: var(--theme-light-text);
  --aha-button--border-color: var(--theme-button-success);
  --aha-button--background-color: var(--theme-button-success);
  --aha-button--outline-color: var(--aha-button--border-color);
}
:host-context(aha-menu[aria-expanded]) :host([kind='success']) {
  --aha-button--color: var(--theme-light-text);
  --aha-button--border-color: var(--theme-button-success);
  --aha-button--background-color: var(--theme-button-success);
  --aha-button--outline-color: var(--aha-button--border-color);
}
:host(
    [kind='success']:where(:not([disabled])):where(:focus-visible, [focused], :hover, [hovered])
  ) {
  --aha-button--border-color: var(--theme-button-success-hovered);
  --aha-button--background-color: var(--theme-button-success-hovered);
}
:host([kind='success']:where(:not([disabled])):where(:active, [active])) {
  --aha-button--border-color: var(--theme-button-success-active);
  --aha-button--background-color: var(--theme-button-success-active);
}

/* Danger button */
:host([kind='danger']) {
  --aha-button--color: var(--theme-light-text);
  --aha-button--border-color: var(--theme-button-danger);
  --aha-button--background-color: var(--theme-button-danger);
  --aha-button--outline-color: var(--aha-button--border-color);
}
:host-context(aha-menu[aria-expanded]) :host([kind='danger']) {
  --aha-button--color: var(--theme-light-text);
  --aha-button--border-color: var(--theme-button-danger);
  --aha-button--background-color: var(--theme-button-danger);
  --aha-button--outline-color: var(--aha-button--border-color);
}
:host([kind='danger']:where(:not([disabled])):where(:focus-visible, [focused], :hover, [hovered])) {
  --aha-button--border-color: var(--theme-button-danger-hovered);
  --aha-button--background-color: var(--theme-button-danger-hovered);
}
:host([kind='danger']:where(:not([disabled])):where(:active, [active])) {
  --aha-button--border-color: var(--theme-button-danger-active);
  --aha-button--background-color: var(--theme-button-danger-active);
}

/* Warning button */
:host([kind='warning']) {
  --aha-button--color: var(--theme-light-text);
  --aha-button--border-color: var(--theme-button-warning);
  --aha-button--background-color: var(--theme-button-warning);
  --aha-button--outline-color: var(--theme-button-warning);
}
:host-context(aha-menu[aria-expanded]) :host([kind='warning']) {
  --aha-button--color: var(--theme-light-text);
  --aha-button--border-color: var(--theme-button-warning);
  --aha-button--background-color: var(--theme-button-warning);
  --aha-button--outline-color: var(--theme-button-warning);
}
:host(
    [kind='warning']:where(:not([disabled])):where(:focus-visible, [focused], :hover, [hovered])
  ) {
  --aha-button--border-color: var(--theme-button-warning-hovered);
  --aha-button--background-color: var(--theme-button-warning-hovered);
}
:host([kind='warning']:where(:not([disabled])):where(:active, [active])) {
  --aha-button--border-color: var(--theme-button-warning-active);
  --aha-button--background-color: var(--theme-button-warning-active);
}

/* "Link" button */
:host([kind='link']) {
  padding: 0;
  border: 0;
  vertical-align: inherit;
  line-height: inherit;
  box-shadow: none;
  min-width: auto;
  width: auto;
  --aha-button--color: var(--theme-link-text);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host-context(aha-menu[aria-expanded]) :host([kind='link']) {
  padding: 0;
  border: 0;
  vertical-align: inherit;
  line-height: inherit;
  box-shadow: none;
  width: auto;
  --aha-button--color: var(--theme-link-text);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host(
    [kind='link']:where(:not([disabled])):where(
        :focus-visible,
        [focused],
        :hover,
        [hovered],
        :active,
        [active]
      )
  ) {
  --aha-button--border-color: var(--theme-button-primary);
  --aha-button--text-decoration: underline;
}

:host([kind='link']:where(:not([disabled])):where(:focus-visible, [focused])) {
  box-shadow: var(--box-shadow-focus-ring);
}
/* Add underline for 'link' when hovered/focused. */
:host(
    :where([kind='link']):where(:not([disabled])):where(
        :focus-visible,
        [focused],
        :hover,
        [hovered]
      )
  )
  :where(#content) {
  text-decoration: var(--aha-button--text-decoration);
}

:host([kind='plain']) {
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host-context(aha-menu[aria-expanded]) :host([kind='plain']) {
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}

:host([kind='ghost']) {
  --aha-button--color: var(--theme-link-text);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host-context(aha-menu[aria-expanded]) :host([kind='ghost']) {
  --aha-button--color: var(--theme-link-text);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host([kind='ghost']:where(:not([disabled])):where(:focus-visible, [focused], :hover, [hovered])) {
  --aha-button--color: var(--theme-link-text-dark);
  --aha-button--border-color: var(--theme-button-ghost-hovered);
  --aha-button--background-color: var(--theme-button-ghost-hovered);
}
:host([kind='ghost']:where(:not([disabled])):where(:active, [active])) {
  --aha-button--color: var(--theme-link-text-dark);
  --aha-button--border-color: var(--theme-button-ghost-active);
  --aha-button--background-color: var(--theme-button-ghost-active);
}

:host([kind='icon']) {
  --aha-button--color: var(--theme-secondary-text);
  padding: calc(var(--aha-button--padding-vertical) / 2)
    calc(var(--aha-button--padding-vertical) / 2);
  font-size: calc(var(--aha-button--font-size) * 1.2);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host-context(aha-menu[aria-expanded]) :host([kind='icon']) {
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host([kind='icon']:where(:not([disabled])):where(:focus-visible, [focused], :hover, [hovered])) {
  --aha-button--border-color: var(--theme-button-gray-hovered);
  --aha-button--background-color: var(--theme-button-gray-hovered);
}
:host([kind='icon']:where(:not([disabled])):where(:active, [active])) {
  --aha-button--border-color: var(--theme-button-gray-active);
  --aha-button--background-color: var(--theme-button-gray-active);
}

:host([outline]) {
  --aha-button--color: var(--aha-button--outline-color, var(--aha-button--border-color));
  --aha-button--background-color: transparent;
}

/* ------------ DEPRECATED STYLE ------------- */
/* "menu-action" button */
:host([kind='menu-action']) {
  min-width: auto;
  padding: 0.25em 0.75em;
  line-height: 1.25;
  min-width: auto;
  border-width: 0;
  margin: -0.25em 0;
  font-size: 11px;

  --aha-button--color: var(--theme-link-text);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host-context(aha-menu[aria-expanded]) :host([kind='menu-action']) {
  min-width: auto;
  padding: 0.25em 0.75em;
  line-height: 1.25;
  min-width: auto;
  border-width: 0;
  margin: -0.25em 0;
  font-size: 11px;

  --aha-button--color: var(--theme-link-text);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host(
    [kind='menu-action']:where(:not([disabled])):where(
        :hover,
        [hovered],
        :focus-visible,
        [focused],
        :active,
        [active]
      )
  ) {
  --aha-button--color: var(--theme-link-text);
  --aha-button--border-color: var(--theme-list-item-active-background);
  --aha-button--background-color: var(--theme-list-item-active-background);
}

/* ------------ DEPRECATED STYLE ------------- */
/* "menu-item" button */
:host([kind='menu-item']) {
  --aha-button--color: var(--theme-text);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host-context(aha-menu[aria-expanded]) :host([kind='menu-item']) {
  --aha-button--color: var(--theme-text);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host(
    [kind='menu-item']:where(:not([disabled])):where(
        :hover,
        [hovered],
        :focus-visible,
        [focused],
        :active,
        [active]
      )
  ) {
  --aha-button--color: var(--theme-button);
  --aha-button--border-color: var(--theme-button-primary);
  --aha-button--background-color: var(--theme-button-primary);
}

/* ------------ DEPRECATED STYLE ------------- */
/* "grouped-menu-item" button */
:host([kind='grouped-menu-item']) {
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host-context(aha-menu[aria-expanded]) :host([kind='grouped-menu-item']) {
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host(
    [kind='grouped-menu-item']:where(:not([disabled])):where(
        :hover,
        [hovered],
        :focus-visible,
        [focused],
        :active,
        [active]
      )
  ) {
  --aha-button--border-color: var(--theme-list-item-active-background);
  --aha-button--background-color: var(--theme-list-item-active-background);
}

/* ------------ DEPRECATED STYLE ------------- */
/* "Attribute" button */
:host([kind='attribute']) {
  --aha-button--color: var(--theme-menu-hover-background);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host-context(aha-menu[aria-expanded]) :host([kind='atttribute']) {
  --aha-button--color: var(--theme-menu-hover-background);
  --aha-button--border-color: transparent;
  --aha-button--background-color: transparent;
}
:host(
    [kind='attribute']:where(:not([disabled])):where(
        :hover,
        [hovered],
        :focus-visible,
        [focused],
        :active,
        [active]
      )
  ) {
  --aha-button--color: var(--theme-menu-active-background);
  --aha-button--border-color: var(--theme-button-primary);
}
